import { useState, useEffect, useRef } from 'react';
import { Globe, Users, Network, BookOpen, Package } from 'lucide-react';
import React, { createRef } from 'react';

export default function Activity() {
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);
  const videoRefs = useRef([]);
  
  const activities = [
    {
      title: "Bilateral Trade Missions and Forums",
      // icon: <Globe className="w-16 h-16 text-blue-600" />,
      description: "Organize trade missions and business forums to connect entrepreneurs, investors, and policymakers from Europe and India, showcasing sectors like Technology, Agriculture, Tourism, Renewable Energy, Trade, Staffing Services, Fashion, Logistics, and more.",
      video: "/videos/trade-mission.mp4",
      color: "from-blue-500 to-blue-600"
    },
    {
      title: "Cultural Exchange Programs",
      // icon: <Users className="w-16 h-16 text-green-600" />,
      description: "Host cultural festivals, art exhibitions, culinary experiences, and language workshops to deepen cultural appreciation and build interpersonal connections.",
      video: "/videos/cultural-exchange.mp4",
      color: "from-green-500 to-green-600"
    },
    {
      title: "Stakeholder Networking Events",
      // icon: <Network className="w-16 h-16 text-purple-600" />,
      description: "Facilitate regular networking events, roundtables, and workshops for businesses, academics, and government officials to discuss opportunities and address challenges in the partnership.",
      video: "/videos/networking-events.mp4",
      color: "from-purple-500 to-purple-600"
    },
    {
      title: "Skill Development and Knowledge Sharing",
      // icon: <BookOpen className="w-16 h-16 text-orange-600" />,
      description: "Launch initiatives such as training programs, technology transfers, and academic collaborations to exchange expertise in areas like IT, green energy, and urban planning.",
      video: "/videos/skill-development.mp4",
      color: "from-orange-500 to-orange-600"
    },
    {
      title: "Promotion of Regional Specialties",
      // icon: <Package className="w-16 h-16 text-red-600" />,
      description: "Develop campaigns to promote India unique offerings—such as textiles, spices, and tourism—within the Netherlands, and vice versa, through curated events, trade shows, and digital platforms.",
      video: "/videos/regional-promotion.mp4",
      color: "from-red-500 to-red-600"
    }
  ];

  // Initialize section and video refs
  useEffect(() => {
    sectionRefs.current = Array(activities.length).fill().map((_, i) => sectionRefs.current[i] || createRef());
    videoRefs.current = Array(activities.length).fill().map((_, i) => videoRefs.current[i] || createRef());
  }, [activities.length]);

  // Scroll observation
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      let newActiveSection = 0;
      sectionRefs.current.forEach((ref, index) => {
        if (ref && ref.current) {
          const offsetTop = ref.current.offsetTop;
          const height = ref.current.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            newActiveSection = index;
          }
        }
      });
      
      setActiveSection(newActiveSection);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to section function
  const scrollToSection = (index) => {
    sectionRefs.current[index]?.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative">
      {/* Fixed navigation dots */}
      <div className="fixed right-4 md:right-8 top-1/2 transform -translate-y-1/2 z-10 hidden md:block">
        <div className="flex flex-col space-y-4">
          {activities.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToSection(index)}
              className={`w-4 h-4 rounded-full transition-all duration-300 border-2 ${
                activeSection === index 
                  ? 'bg-blue-600 border-blue-600 scale-125' 
                  : 'bg-white border-gray-400 hover:border-blue-400'
              }`}
              aria-label={`Go to section ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Header */}
      <div className="text-white py-12 md:py-20 px-4 md:px-6" style={{ backgroundColor: '#76738e' }}>
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 md:mb-6">Top 5 Tactical Activities</h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">Building stronger connections between Europe and India through strategic initiatives and partnerships</p>
        </div>
      </div>

      {/* Activity sections */}
      {activities.map((activity, index) => (
        <section 
          key={index}
          ref={el => sectionRefs.current[index] = {current: el}}
          className={`min-h-screen flex items-center py-12 md:py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="container mx-auto px-4 md:px-6">
            <div className={`flex flex-col md:flex-row ${index % 2 === 0 ? '' : 'md:flex-row-reverse'} items-center`}>
              {/* Video side */}
              <div className="md:w-1/2 mb-8 md:mb-0">
                <div className="relative rounded-lg overflow-hidden shadow-xl">
                  {/* Continuously playing video without controls */}
                  <video
                    ref={el => videoRefs.current[index] = {current: el}}
                    src={activity.video}
                    className="w-full h-full object-cover rounded-lg"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                  
                  <div className={`absolute -bottom-4 -right-4 md:-right-8 p-6 rounded-full bg-gradient-to-br ${activity.color} shadow-lg`}>
                    {activity.icon}
                  </div>
                </div>
              </div>
              
              {/* Content side */}
              <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pl-16' : 'md:pr-16'}`}>
                <div className="flex items-center mb-4">
                  <span className="text-6xl font-bold text-gray-200 mr-4">{index + 1}</span>
                  <h2 className="text-3xl font-bold text-gray-800">{activity.title}</h2>
                </div>
                <div className="w-24 h-1 bg-blue-600 mb-6"></div>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">{activity.description}</p>
                <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md">
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}